/**
 * ONE TAP — the gun.
 *
 * There is one of these per team and the entire game orbits it. Players stare at it while
 * it sits in their hands, while it tumbles through the air, while it lies on the floor
 * twenty metres away being contested. It is the ammo, the medkit and the objective, so it
 * gets more geometry and more love than anything else in the project.
 *
 * ART: a chunky stylised sidearm with exaggerated proportions — a **dark slate shell**,
 * ink-black rubber, big team-coloured side plates, an oversized dorsal fin and an absurdly
 * readable muzzle ring. Every hard edge is bevelled so the inverted-hull outline catches a
 * chamfer instead of reading as an untextured cube (DESIGN.md §4, §7).
 *
 * WHY THE BODY IS DARK. The arena is white architecture under a bright sky, so the gun is the
 * one object in frame that is allowed to be a dark mass — and it has to be, twice over:
 * DESIGN.md §4 warns that "a pale glow on a pale world is invisible", so the amber chamber
 * needs something to burn against; and a pale gun on a pale wall has no silhouette at all.
 * The shell therefore sits on the dark end of the palette (`ink` lifted slightly toward the
 * cool `shadow` blue, never crushed to black) in *both* chamber states, so the object's
 * outline is a constant, instantly-recognisable shape and only the light inside it changes.
 *
 * THE ONE JOB THIS FILE MUST NOT FAIL AT (DESIGN.md §4):
 *
 *   > A loaded gun glows hot yellow. An empty gun is dead grey and lifeless. Across the
 *   > entire arena, at any distance, every player must answer "who is armed right now?"
 *   > in one glance.
 *
 * So `styleGun()` does not nudge a tint. It swaps the object between two visual identities:
 *
 *   CHAMBERED — the chamber flag stands up off the spine, an energised core spins behind a
 *               glowing chamber window, twin energy rings burn around the barrel, the top
 *               rail is a hot filament that breathes, the exhaust port glows so the state is
 *               readable even from directly behind, the bore glows so it is readable from
 *               directly in *front*, and a pair of additive sprites sit proud of the top
 *               edge so the gun is legible as a bright dot at maximum arena distance.
 *   EMPTY     — the flag drops flat behind the breech, the core collapses to a dead sliver,
 *               the rings shrink out of the silhouette entirely, the window goes cold and
 *               near-opaque, every emissive is switched off (not dimmed — off), the amber
 *               parts go flat unlit grey, the bore goes black, and the shell settles a
 *               further step toward ink so a spent gun reads as a cold object.
 *
 * The flag is doing work none of the rest can: it is the only cue that changes the object's
 * OUTLINE, so it survives greyscale, colour-blindness, eight pixels of screen space, and a
 * frame where the amber is competing with direct sunlight. Hue alone is not a state channel.
 *
 * There is an amber tell on every one of the six faces of the object's bounding box —
 * rail on top, pip underneath, window on both flanks, bore in front, exhaust port behind —
 * because "who is armed?" has to be answerable from whatever angle the gun happens to
 * present, including the angle of a gun tumbling through the air mid-pass.
 *
 * PERFORMANCE: the shell is ~40 authored parts but only a handful of draw calls. Anything
 * that never moves is baked into one merged mesh per material at build time; only the slide
 * (it racks), the core, the shards, the barrel rings and the ammo pip stay separate, because
 * those are the pieces that actually animate.
 *
 * CONVENTION: the gun is built pointing down **-Z** with +Y up, which matches
 * `dirFromYawPitch()` (yaw 0 looks down -Z). The origin sits at the hand grip point, so the
 * mesh can be parented straight to a hand bone with an identity transform, and a tumbling
 * gun spins about a point close to its centre of mass.
 *
 * Client-only: imports `three`. Never import from `src/shared`.
 */

import * as THREE from 'three';
import { PALETTE, TEAM_COLORS } from '@shared/constants';
import { clamp01, TAU } from '@shared/math';
import { makeToonMaterial, attachOutline, setBloomMask } from '@client/render/toon';
import {
  MeshBatcher,
  bevelBox,
  disposeObject3D,
  lathe,
  markShared,
  mergeGeometryList,
  mixHex,
  place,
  radialTexture,
  taperBox,
} from '@client/entities/anim';

// ---------------------------------------------------------------------------
// Proportions. Exaggerated on purpose: a realistically-scaled pistol is a smudge at
// 40 metres, and this object has to be legible across the whole arena.
// ---------------------------------------------------------------------------

/** Nose of the muzzle ring, in metres forward of the grip. */
const MUZZLE_Z = -0.345;
/** Rear face of the receiver. */
const BREECH_Z = 0.2;
/** Front of the receiver, where the barrel shroud takes over. */
const FRAME_FRONT_Z = -0.19;

const FRAME_W = 0.122;
const FRAME_H = 0.144;
/** Receiver centre height above the hand. */
const FRAME_Y = 0.068;

/** Deliberately tall. The fat top mass is most of what a sidearm silhouette *is* at 8px. */
const SLIDE_H = 0.074;
const SLIDE_Y = FRAME_Y + FRAME_H * 0.5 + SLIDE_H * 0.5 - 0.008;

const BARREL_R = 0.056;
const BARREL_Y = FRAME_Y + 0.012;
const GRIP_RAKE = -0.31;

/** Where the energy core lives — the visual centre of the whole object. */
const CHAMBER_Z = 0.028;
const CHAMBER_Y = FRAME_Y + 0.004;

/**
 * CHAMBER FLAG — the *shape* channel of the chamber read.
 *
 * Hue is not enough and never was. A player answering "is that gun live?" is doing it at
 * eight pixels, in peripheral vision, against white architecture, and often in a frame where
 * amber is already competing with sunlight; colour-blind players never get the cue at all.
 * So the loaded state also changes the object's OUTLINE: a blade stands proud of the spine
 * when a round is up, and drops flat behind the breech when the chamber is spent.
 *
 * It sits at the rear of the slide, above the crest fin, so the raised flag is the highest
 * point of the silhouette and the drop is a change to the gun's profile rather than to a
 * detail buried inside it.
 */
const FLAG_Z = 0.176;
const FLAG_LEN = 0.086;
/** Pivot height above the top face of the slide — clear of the crest fin it folds over. */
const FLAG_PIVOT_LIFT = 0.07;
/** Raised: near-vertical, raked back a touch so it does not read as an antenna. */
const FLAG_UP = 0.1;
/** Dropped: laid forward flat along the spine. Same part, unmistakably different profile. */
const FLAG_DOWN = -1.46;

// ---------------------------------------------------------------------------
// The shell palette. Everything here is derived from PALETTE so the gun can never drift
// out of the art direction — but note which end of the ramp it sits on.
//
// `ink` (#2A3242) is the darkest value the game permits (DESIGN.md §4: never crush to
// black). The gun body lives right there, lifted a fifth of the way toward the cool
// `shadow` blue so it stays a *saturated slate* rather than a dead charcoal, and so it
// separates from its own ink outline.
// ---------------------------------------------------------------------------

/** Primary mass: receiver, slide, barrel shroud. The silhouette. */
const BODY_LIFT = 0.17;
/** Secondary chamfer panels — a step lighter, so bevels catch the key and read as form. */
const BODY_LIGHT_LIFT = 0.4;
/** Grip rubber, serrations, bezels: the darkest parts, sitting on ink itself. */
const BODY_DEEP_LIFT = 0.0;

/** Extra step toward ink when the chamber is spent. A cold object, not merely a dim one. */
const EMPTY_DARKEN = 0.42;

const bodyHex = (lift: number): number => mixHex(PALETTE.ink, PALETTE.shadow, lift);

/**
 * What the emissive parts become when the chamber is spent: EMPTY grey, taken a third of the
 * way down toward ink. Straight `PALETTE.empty` is *lighter* than the shell, so a dead gun's
 * rail and rings punched bright holes through its own silhouette — the same failure the dark
 * body exists to prevent, arriving from the other direction. This still reads as flat unlit
 * metal, which is the whole brief for EMPTY, without out-glowing the object it sits on.
 */
const EMPTY_METAL = mixHex(PALETTE.empty, PALETTE.ink, 0.35);

/**
 * Emissive strengths, in units of the LOADED colour.
 *
 * These look low for something described as "hot", and that is deliberate. The post chain
 * adds `BLOOM_STRENGTH` of a blurred copy of every masked emitter back on top of itself and
 * *then* tonemaps, so a fat emissive area is multiplied by roughly 1.5 before it ever
 * reaches the shoulder of the curve. Driven at the old 1.3 the amber's red channel clipped
 * a long way past 1.0 while green followed it up, and the top rail — the one element a
 * carrying player has in peripheral vision for the whole match — resolved as a WHITE stripe.
 *
 * White is the arena. An indicator that has bleached to the colour of the walls has stopped
 * indicating (DESIGN.md §4). Tuned instead so red saturates and green lands near 0.6 after
 * bloom and tonemap, which is display amber: hot, unmistakably warm, still `loaded`.
 */
const HOT_EMISSIVE = 0.62;
const WARM_EMISSIVE = 0.36;
const DEAD_EMISSIVE = 0.0;

/** Depth of the chambered breathing pulse, as a fraction of HOT_EMISSIVE. */
const PULSE_DEPTH = 0.18;

/**
 * How far the chambered emissive parts' *albedo* is pulled toward ink. The emissive term is
 * what makes them amber; the albedo is only there so the shapes still read as objects when
 * the chamber is spent. Keeping it dark stops the lit diffuse from stacking on the emissive
 * and desaturating the whole element.
 */
const ENERGY_ALBEDO_MIX = 0.62;

/**
 * Additive glow sprites: a tight core and a wide halo, both in LOADED amber.
 *
 * Kept modest in world space — sized to look impressive up close they become a wash that
 * additively bleaches the gun's own dark body back to pale tan, which is the exact failure
 * the dark shell exists to fix.
 *
 * Two things about them are load-bearing:
 *
 *  - The falloff is SOFT. The previous texture was `(1-r)^3.7`, which put every usable
 *    texel inside the middle quarter of the quad, so a 44cm halo had an effective radius of
 *    about five centimetres — smaller than the receiver it was sitting inside. Depth-tested
 *    against the gun's own shell, it was invisible from every angle, and the long-range
 *    "who is armed?" read it exists to carry simply did not happen.
 *  - They sit on the TOP EDGE of the slide, not buried at the chamber. These are depth
 *    tested on purpose (a glow that shines through walls is a wall-hack, not a look), so a
 *    sprite centred inside the shell is occluded by the shell. On the top edge roughly half
 *    of it always overhangs the silhouette and reaches the frame.
 */
const GLOW_CORE_SCALE = 0.22;
const GLOW_HALO_SCALE = 0.5;
/** Halo opacity when chambered. Low: it is a soft aura, not a light source. */
const GLOW_HALO_OPACITY = 0.24;
/** Falloff exponent shaping term for the glow sprite. Lower = wider usable disc. */
const GLOW_SOFTNESS = 0.55;
/** Sprite centre: the top edge of the slide, where the silhouette ends and the frame begins. */
const GLOW_Y = SLIDE_Y + SLIDE_H * 0.5 + 0.02;

// ---------------------------------------------------------------------------
// Shared textures — one canvas each, for every gun ever built.
// ---------------------------------------------------------------------------

let glowTexture: THREE.Texture | null = null;
function getGlowTexture(): THREE.Texture {
  if (!glowTexture) glowTexture = markShared(radialTexture(0.0, GLOW_SOFTNESS, 128));
  return glowTexture;
}

// ---------------------------------------------------------------------------
// Part registry
// ---------------------------------------------------------------------------

interface GunParts {
  frame: THREE.Material[];
  dark: THREE.Material[];
  team: THREE.Material[];
  energy: THREE.Material[];
  /** The bore disc. Amber when a round is up the spout, black when it is not. */
  bore: THREE.Material;
  glass: THREE.Material[];
  /** The slide assembly. Racks backward on a catch and twitches on a dry fire. */
  slide: THREE.Group;
  slideRestZ: number;
  /** The spinning energy spindle inside the chamber. */
  core: THREE.Object3D;
  /** Orbiting shards around the core — the "this thing is alive" tell. */
  shards: THREE.Object3D;
  /** Barrel rings; they shrink out of the silhouette when empty. */
  rings: THREE.Object3D;
  /** Chamber flag; it stands up when a round is chambered and drops when it is not. */
  flag: THREE.Object3D;
  /** Ammo indicator pip on the magazine floorplate. */
  pip: THREE.Object3D;
  glow: THREE.Sprite;
  /** Soft bloom halo behind the glow — sells "this thing is radiating" up close. */
  halo: THREE.Sprite;
  /** Bore exit, in gun-local space — where a muzzle flash belongs. */
  muzzle: THREE.Vector3;
  /** Ejection port, in gun-local space — where brass comes out. */
  eject: THREE.Vector3;
  /** Cache so restyling every frame is free when nothing changed. */
  styleKey: string;
  chambered: boolean;
  team_: number;
}

function partsOf(g: THREE.Group): GunParts | null {
  return (g.userData['gunParts'] as GunParts | undefined) ?? null;
}

const _pm = new THREE.Matrix4();
const _cm = new THREE.Matrix4();

/** Compose a child placement under an already-composed parent matrix. */
function under(parent: THREE.Matrix4, child: THREE.Matrix4): THREE.Matrix4 {
  return _cm.multiplyMatrices(parent, child);
}

// ---------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------

/**
 * Build a fresh gun. Materials are **not** shared between calls — the two team guns are
 * independently tinted and independently lit, and a held gun and a ground gun are different
 * objects with different chamber states.
 *
 * Everything you might want to poke afterwards lives in `userData.gunParts`; use
 * `gunAttachPoints()` for the muzzle / ejection port / slide.
 */
export function buildGunMesh(): THREE.Group {
  const root = new THREE.Group();
  root.name = 'gun';

  // --- materials -----------------------------------------------------------
  // Dark slate shell, ink rubber, team plates, energy, chamber glass. The rim on the body
  // is generous: a warm sun-wrap along the top edges is what keeps a dark object from
  // reading as a hole punched in a bright frame.
  const matFrame = makeToonMaterial({ color: bodyHex(BODY_LIFT), rim: 0.62 });
  const matFrameDeep = makeToonMaterial({ color: bodyHex(BODY_LIGHT_LIFT), rim: 0.4 });
  const matDark = makeToonMaterial({ color: bodyHex(BODY_DEEP_LIFT), rim: 0.24 });
  const matTeam = makeToonMaterial({
    color: TEAM_COLORS[0],
    emissive: TEAM_COLORS[0],
    emissiveIntensity: 0.28,
    rim: 0.5,
  });
  const matTeamDeep = makeToonMaterial({
    color: TEAM_COLORS[0],
    emissive: TEAM_COLORS[0],
    emissiveIntensity: 0.14,
  });
  const matEnergy = makeToonMaterial({
    color: PALETTE.loaded,
    emissive: PALETTE.loaded,
    emissiveIntensity: HOT_EMISSIVE,
  });
  const matEnergySoft = makeToonMaterial({
    color: PALETTE.loaded,
    emissive: PALETTE.loaded,
    emissiveIntensity: WARM_EMISSIVE,
  });
  /**
   * The bore. Its own material because it is the only emissive on the gun that must go
   * genuinely BLACK when spent rather than to EMPTY grey — you are looking down a hole, and
   * a hole with a pale disc in it stops reading as a hole.
   */
  const matBore = makeToonMaterial({
    color: PALETTE.loaded,
    emissive: PALETTE.loaded,
    emissiveIntensity: WARM_EMISSIVE,
  });
  // These three are unambiguous emitters. Say so explicitly instead of inheriting a mask
  // from `emissiveIntensity`: the post chain gates its bloom mask at 0.6 to keep ordinary
  // alpha-blended geometry out, and WARM_EMISSIVE sits under that gate — which would have
  // quietly excluded the shards and the bore from the one effect they are built around.
  setBloomMask(matEnergy, 1);
  setBloomMask(matEnergySoft, 1);
  setBloomMask(matBore, 1);
  const matGlass = new THREE.MeshBasicMaterial({
    color: PALETTE.loaded,
    transparent: true,
    opacity: 0.8,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
    toneMapped: false,
  });

  // =========================================================================
  // STATIC SHELL — batched into one mesh per material
  // =========================================================================
  const shell = new MeshBatcher();
  const frameDepth = BREECH_Z - FRAME_FRONT_Z;
  const frameMidZ = (BREECH_Z + FRAME_FRONT_Z) * 0.5;

  // Receiver: the main body, breech to shroud.
  shell.add(matFrame, bevelBox(FRAME_W, FRAME_H, frameDepth, 0.017, 0.028), place(0, FRAME_Y, frameMidZ));

  // Barrel shroud: lathed, flaring into the muzzle crown.
  const shroudLen = 0.17;
  shell.add(
    matFrame,
    lathe(
      [
        [BARREL_R * 0.96, 0],
        [BARREL_R, 0.018],
        [BARREL_R * 0.92, 0.06],
        [BARREL_R * 0.92, 0.116],
        [BARREL_R * 1.16, 0.132],
        [BARREL_R * 1.2, 0.152],
        [BARREL_R * 0.98, shroudLen],
        [BARREL_R * 0.6, shroudLen],
        [BARREL_R * 0.42, shroudLen - 0.02],
      ],
      18,
      true,
    ),
    place(0, BARREL_Y, -0.185),
  );

  // Rear cap / hammer housing.
  shell.add(matFrameDeep, bevelBox(FRAME_W - 0.024, 0.072, 0.055, 0.014, 0.02), place(0, SLIDE_Y + 0.012, BREECH_Z - 0.01, 0.2));

  // Bore: a dark disc so the muzzle is not an open-ended tube...
  shell.add(matDark, new THREE.CircleGeometry(BARREL_R * 0.62, 14), place(0, BARREL_Y, MUZZLE_Z + 0.02, 0, Math.PI));
  // ...with a live round sitting in it. This is the only chamber tell visible from *directly
  // down the barrel*, which is precisely the angle the person about to be shot is looking
  // from. A gun pointed at you is either an amber eye or a black hole, and that difference
  // is worth its own thirteen triangles.
  shell.add(matBore, new THREE.CircleGeometry(BARREL_R * 0.42, 14), place(0, BARREL_Y, MUZZLE_Z + 0.012, 0, Math.PI));

  // Muzzle crown ring — the biggest single silhouette read at the front of the gun.
  shell.add(matTeam, new THREE.TorusGeometry(BARREL_R * 1.36, 0.022, 8, 20), place(0, BARREL_Y, MUZZLE_Z + 0.012));

  // Muzzle fins — four blades around the crown.
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * TAU + Math.PI / 4;
    shell.add(
      matTeamDeep,
      bevelBox(0.019, 0.058, 0.066, 0.005, 0.009),
      place(Math.cos(a) * BARREL_R * 1.26, BARREL_Y + Math.sin(a) * BARREL_R * 1.26, -0.292, 0, 0, a - Math.PI / 2),
    );
  }

  // Chamber window bezel — four thin dark bars per flank framing the glass, so the aperture
  // reads as cut into the shell rather than painted on it.
  const bezelX = FRAME_W * 0.5 + 0.003;
  const bezelSpecs: readonly (readonly [number, number, number, number])[] = [
    // [sizeY, sizeZ, offsetY, offsetZ]
    [0.012, 0.172, 0.05, 0],
    [0.012, 0.172, -0.05, 0],
    [0.112, 0.013, 0, 0.08],
    [0.112, 0.013, 0, -0.08],
  ];
  for (const side of [1, -1]) {
    for (const [sy, sz, oy, oz] of bezelSpecs) {
      shell.add(matDark, bevelBox(0.009, sy, sz, 0.002, 0.003), place(side * bezelX, CHAMBER_Y + oy, CHAMBER_Z + oz));
    }
  }

  // Team side plates — the dominant "whose gun is this" read. Big, flat, unmissable.
  for (const side of [1, -1]) {
    shell.add(matTeam, bevelBox(0.013, 0.088, 0.115, 0.004, 0.02), place(side * (FRAME_W * 0.5 + 0.004), FRAME_Y - 0.004, -0.125));
    shell.add(
      matFrameDeep,
      bevelBox(0.011, 0.016, 0.075, 0.003, 0.006),
      place(side * (FRAME_W * 0.5 + 0.009), FRAME_Y + 0.018, -0.13, 0.26),
    );
    // Raked louvres behind the chamber.
    for (let i = 0; i < 3; i++) {
      shell.add(
        matDark,
        bevelBox(0.01, 0.04, 0.013, 0.002, 0.004),
        place(side * (FRAME_W * 0.5 + 0.002), FRAME_Y - 0.012, 0.118 + i * 0.026, 0.32),
      );
    }
  }

  // Exhaust port on the back face — glows when charged, so you can read the state of a gun
  // that is flying *away* from you.
  shell.add(matEnergy, new THREE.CircleGeometry(0.024, 12), place(0, SLIDE_Y + 0.014, BREECH_Z + 0.017));

  // Chamber windows.
  for (const side of [1, -1]) {
    // Sized right out to the bezels. The window is the single largest amber area on the gun
    // and the flank is the angle you see a *teammate's* gun from, so every square centimetre
    // of it is buying arena-wide legibility.
    shell.add(matGlass, new THREE.PlaneGeometry(0.164, 0.096), place(side * (FRAME_W * 0.5 + 0.001), CHAMBER_Y, CHAMBER_Z, 0, side * (Math.PI / 2)));
  }

  // --- grip (baked out of its own local frame) -----------------------------
  const gripM = _pm.copy(place(0, -0.012, 0.028, GRIP_RAKE));
  shell.add(matDark, taperBox(0.094, 0.256, 0.126, 1.14, 0.014), under(gripM, place(0, -0.125, 0)));
  // Backstrap in team colour — visible from behind, which is exactly the angle a teammate
  // watching your back sees.
  shell.add(matTeamDeep, bevelBox(0.056, 0.2, 0.016, 0.004, 0.012), under(gripM, place(0, -0.125, 0.06)));
  // Finger grooves.
  for (let i = 0; i < 3; i++) {
    shell.add(
      matFrameDeep,
      new THREE.TorusGeometry(0.03, 0.006, 5, 10, Math.PI),
      under(gripM, place(0, -0.062 - i * 0.055, -0.045, 0, Math.PI / 2, Math.PI / 2)),
    );
  }
  // Magazine floorplate.
  shell.add(matFrameDeep, bevelBox(0.09, 0.024, 0.118, 0.006, 0.012), under(gripM, place(0, -0.258, 0.004)));

  // --- trigger group -------------------------------------------------------
  shell.add(matDark, new THREE.TorusGeometry(0.056, 0.012, 6, 14, Math.PI * 1.15), place(0, -0.048, -0.03, 0, Math.PI / 2, Math.PI * 0.62));
  shell.add(matFrameDeep, bevelBox(0.017, 0.05, 0.014, 0.003, 0.005), place(0, -0.042, -0.018, -0.16));

  const shellMeshes = shell.flush(root, { name: 'gunShell' });
  for (const m of shellMeshes) {
    // Outline the opaque shell only. Outlining the additive chamber glass would draw a black
    // box around the brightest thing on the gun, and the bore inlay is a flat disc sunk into
    // a barrel — a hull around it can only ever be an ink ring eating the glow.
    if (m.material !== matGlass && m.material !== matBore) attachOutline(m);
  }

  // =========================================================================
  // SLIDE — a separate assembly, because it moves
  // =========================================================================
  const slide = new THREE.Group();
  slide.name = 'slide';
  root.add(slide);

  const slideBatch = new MeshBatcher();
  slideBatch.add(matFrame, bevelBox(FRAME_W - 0.014, SLIDE_H, frameDepth + 0.03, 0.013, 0.02), place(0, SLIDE_Y, frameMidZ - 0.012));
  // Wedge nose — a chamfer block that keeps the front from looking sawn off.
  slideBatch.add(matFrameDeep, bevelBox(FRAME_W - 0.02, SLIDE_H + 0.02, 0.07, 0.02, 0.02), place(0, SLIDE_Y - 0.008, -0.2, -0.14));
  // Cocking serrations.
  for (let i = 0; i < 5; i++) {
    slideBatch.add(matDark, bevelBox(FRAME_W - 0.008, SLIDE_H * 0.78, 0.011, 0.003, 0.004), place(0, SLIDE_Y, 0.09 + i * 0.023));
  }
  // Sights — chunky and tall, purely for silhouette. Nobody aims down them; this is an FPS
  // with a crosshair. They exist so the top edge of the gun is not a straight line.
  slideBatch.add(matDark, bevelBox(0.016, 0.05, 0.018, 0.004, 0.006), place(0, SLIDE_Y + SLIDE_H * 0.5 + 0.024, -0.175));
  for (const side of [1, -1]) {
    slideBatch.add(matDark, bevelBox(0.014, 0.042, 0.02, 0.004, 0.006), place(side * 0.03, SLIDE_Y + SLIDE_H * 0.5 + 0.02, 0.15));
  }
  // Dorsal crest fin — a big flat blade on the spine, in SLATE rather than team colour.
  // It used to be a team-coloured plate, which put a large saturated panel directly beside
  // the amber rail and the chamber flag: two loud colours competing on the one edge of the
  // gun a carrying player has in permanent peripheral vision. Team identity is answered by
  // the flanks and the muzzle crown, where nothing else is asking a question; the spine
  // belongs to the chamber (DESIGN.md §4 — white plus ONE accent, spent functionally).
  // Kept low, too: the crest is form, the flag above it is information, and the taller of
  // the two should be the one that means something.
  slideBatch.add(matFrameDeep, bevelBox(0.017, 0.052, 0.152, 0.005, 0.012), place(0, SLIDE_Y + SLIDE_H * 0.5 + 0.032, 0.072, -0.1));
  // Top rail: a hot filament running the length of the slide. A charge bar, seen from the side,
  // and the one part of the gun that stays in peripheral vision the whole time you carry it —
  // which is why it is fat enough to read without being looked at.
  slideBatch.add(matEnergy, bevelBox(0.03, 0.026, 0.3, 0.005, 0.006), place(0, SLIDE_Y + SLIDE_H * 0.5 + 0.007, -0.01));

  for (const m of slideBatch.flush(slide, { name: 'slide' })) attachOutline(m);

  // Chamber flag. Parented to the slide so it rides the racking action — the flag coming up
  // is *caused* by the slide going home on a catch, which is the whole fiction of the rule.
  const flag = new THREE.Group();
  flag.name = 'chamberFlag';
  flag.position.set(0, SLIDE_Y + SLIDE_H * 0.5 + FLAG_PIVOT_LIFT, FLAG_Z);
  slide.add(flag);
  const flagBlade = new THREE.Mesh(bevelBox(0.019, FLAG_LEN, 0.034, 0.004, 0.009), matEnergy);
  flagBlade.name = 'chamberFlagBlade';
  flagBlade.castShadow = false;
  flagBlade.position.set(0, FLAG_LEN * 0.5, 0);
  attachOutline(flagBlade);
  flag.add(flagBlade);

  // =========================================================================
  // ANIMATED ENERGY PARTS
  // =========================================================================
  // The core: a lathed double-cone spindle that spins on the bore axis.
  const core = new THREE.Group();
  core.name = 'core';
  core.position.set(0, CHAMBER_Y, CHAMBER_Z);
  root.add(core);

  const spindle = new THREE.Mesh(
    lathe(
      [
        [0.002, -0.058],
        [0.016, -0.04],
        [0.03, -0.012],
        [0.034, 0],
        [0.03, 0.012],
        [0.016, 0.04],
        [0.002, 0.058],
      ],
      10,
      true,
    ),
    matEnergy,
  );
  spindle.name = 'coreSpindle';
  spindle.castShadow = false;
  core.add(spindle);

  // Three shards orbiting the spindle, merged into one mesh. Cheap, and it makes a loaded
  // gun feel *charged* rather than merely painted yellow.
  const shardGeos: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * TAU;
    const g = new THREE.OctahedronGeometry(0.012, 0);
    g.applyMatrix4(place(Math.cos(a) * 0.036, Math.sin(a) * 0.02, Math.sin(a) * 0.03));
    shardGeos.push(g);
  }
  const shards = new THREE.Mesh(mergeGeometryList(shardGeos), matEnergySoft);
  for (const g of shardGeos) g.dispose();
  shards.name = 'shards';
  shards.castShadow = false;
  core.add(shards);

  // Twin energy rings around the barrel — the chamber state, read at a distance.
  const ringGeos: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 2; i++) {
    const g = new THREE.TorusGeometry(BARREL_R * 1.1, 0.0085, 6, 18);
    g.applyMatrix4(place(0, 0, (-0.215 - i * 0.058) - -0.244));
    ringGeos.push(g);
  }
  const rings = new THREE.Mesh(mergeGeometryList(ringGeos), matEnergy);
  for (const g of ringGeos) g.dispose();
  rings.name = 'energyRings';
  rings.position.set(0, BARREL_Y, -0.244);
  rings.castShadow = false;
  root.add(rings);

  // Ammo pip on the floorplate. One pip, lit or dead: this gun holds exactly one round.
  const pip = new THREE.Mesh(bevelBox(0.03, 0.012, 0.03, 0.003, 0.006), matEnergy);
  pip.name = 'ammoPip';
  pip.castShadow = false;
  under(gripM, place(0, -0.268, -0.03)).decompose(pip.position, pip.quaternion, pip.scale);
  root.add(pip);

  // --- glow ----------------------------------------------------------------
  // Two additive sprites: a tight core and a wide halo. They are the soft aura around a
  // charged gun, and they carry the read against dark and mid-value backgrounds — against
  // white architecture nothing additive can win, which is exactly why the arena read is
  // carried by the *emissive geometry* changing the gun's own pixels from grey to amber,
  // with the bloom pass spreading that a little past the silhouette.
  //
  // Deliberately NOT a THREE.PointLight. A gun mesh is hidden whenever its owner is unarmed
  // and there are up to eight of them alive at once (six rigs, a viewmodel, the world
  // entities); a light that blinks in and out of the scene changes the renderer's light
  // count, and every material in the game recompiles its program when that happens. Emissive
  // plus bloom carries the read at zero risk of a mid-fight hitch. If real illumination is
  // wanted, attach exactly one persistent light per *world* gun entity — see
  // `gunAttachPoints()` — where the count stays constant.
  const makeGlowSprite = (scale: number, opacity: number, name: string): THREE.Sprite => {
    const s = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: getGlowTexture(),
        color: PALETTE.loaded,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: true,
        toneMapped: false,
        opacity,
      }),
    );
    s.name = name;
    // On the top edge of the slide, NOT inside the receiver. Depth testing is on, so a
    // sprite whose centre is buried in the shell is clipped by the shell from every angle;
    // sitting it on the silhouette boundary means half the disc always reaches the frame.
    s.position.set(0, GLOW_Y, CHAMBER_Z);
    s.scale.setScalar(scale);
    root.add(s);
    return s;
  };
  const glow = makeGlowSprite(GLOW_CORE_SCALE, 1, 'gunGlow');
  const halo = makeGlowSprite(GLOW_HALO_SCALE, GLOW_HALO_OPACITY, 'gunHalo');

  const parts: GunParts = {
    frame: [matFrame, matFrameDeep],
    dark: [matDark],
    team: [matTeam, matTeamDeep],
    energy: [matEnergy, matEnergySoft],
    bore: matBore,
    glass: [matGlass],
    slide,
    slideRestZ: slide.position.z,
    core,
    shards,
    rings,
    flag,
    pip,
    glow,
    halo,
    muzzle: new THREE.Vector3(0, BARREL_Y, MUZZLE_Z - 0.02),
    eject: new THREE.Vector3(FRAME_W * 0.5 + 0.01, SLIDE_Y, 0.09),
    styleKey: '',
    chambered: true,
    team_: 0,
  };
  root.userData['gunParts'] = parts;

  // Default to the loaded look; every call site immediately calls styleGun anyway.
  styleGun(root, 0, true);
  return root;
}

/**
 * Attachment points a caller needs to hang effects off the gun: the bore exit, the ejection
 * port, and the slide object itself (so a viewmodel can rack it). All in gun-local space.
 *
 * Not part of the fixed module contract, but far more robust than looking meshes up by name
 * now that the static shell is merged.
 */
export function gunAttachPoints(
  g: THREE.Group,
): { muzzle: THREE.Vector3; eject: THREE.Vector3; slide: THREE.Object3D; slideRestZ: number } | null {
  const parts = partsOf(g);
  if (!parts) return null;
  return { muzzle: parts.muzzle, eject: parts.eject, slide: parts.slide, slideRestZ: parts.slideRestZ };
}

// ---------------------------------------------------------------------------
// Styling — team identity and, far more importantly, chamber state
// ---------------------------------------------------------------------------

/**
 * Drive the gun's two visual identities.
 *
 * `chambered` is the most important boolean in ONE TAP's rendering. Do not soften the
 * difference: a player must never have to squint to work out whether the gun across the
 * arena is a threat or a paperweight.
 *
 * Cheap to call every frame — it early-outs unless (team, chambered) actually changed.
 */
export function styleGun(g: THREE.Group, team: number, chambered: boolean): void {
  const parts = partsOf(g);
  if (!parts) return;

  const t = team === 1 ? 1 : 0;
  const key = `${t}|${chambered ? 1 : 0}`;
  if (parts.styleKey === key) return;
  parts.styleKey = key;
  parts.team_ = t;
  parts.chambered = chambered;

  const teamColor = TEAM_COLORS[t]!;
  /**
   * Diffuse albedo of the emissive parts. When chambered this is a DARK amber, not LOADED
   * amber: the emissive term is what carries the glow, and if the albedo is also full amber
   * then a saturated colour under a 3.0-intensity key adds on top of the emissive and the
   * whole element clips to white. A hot element that has gone white has lost the only thing
   * it was for — nobody reads "amber" off a white ring. Dark albedo, hot emissive, amber.
   * Spent, it swaps to flat unlit grey and lights normally, because then it is just metal.
   */
  const energyColor = chambered ? mixHex(PALETTE.loaded, PALETTE.ink, ENERGY_ALBEDO_MIX) : EMPTY_METAL;

  // Shell. The body stays DARK in both states — that is the point of it (see the file header):
  // the silhouette must be the same recognisable shape whether the gun is a threat or a
  // paperweight, so the state read is carried entirely by the light *inside* the object.
  // A spent gun does settle one further step toward ink, which reads as "cold" rather than
  // as a different weapon.
  const bodyColor = chambered ? bodyHex(BODY_LIFT) : mixHex(bodyHex(BODY_LIFT), PALETTE.ink, EMPTY_DARKEN);
  const bodyLightColor = chambered
    ? bodyHex(BODY_LIGHT_LIFT)
    : mixHex(bodyHex(BODY_LIGHT_LIFT), PALETTE.ink, EMPTY_DARKEN);
  applyColor(parts.frame[0], bodyColor);
  applyColor(parts.frame[1], bodyLightColor);
  // No emissive on the shell, ever. It is tempting to bleed a little amber into the slate so
  // the glow looks "attached" — but a dark body's lit value is small, and even a tenth of an
  // emissive unit doubles it and drags the whole gun to warm khaki. That is the pale-mass
  // failure coming back in through the side door. The shell reflects; only the chamber emits.
  applyEmissive(parts.frame[0], 0x000000, 0);
  applyEmissive(parts.frame[1], 0x000000, 0);

  // Ink rubber never changes: it is already at the darkest value the palette allows, and
  // moving it would only muddy the read.
  applyColor(parts.dark[0], bodyHex(BODY_DEEP_LIFT));

  // Team plates keep their hue in both states but lose their glow when the gun is spent, so
  // an empty gun never competes with a loaded one for attention. They darken toward ink —
  // never toward EMPTY grey, which is *lighter* than the shell and would punch bright holes
  // in the silhouette of a dead gun.
  //
  // Their emissive is deliberately meagre. Team colour is *identity* — it answers "whose
  // gun is this?", which matters for a steal and never in a hurry. Chamber state is
  // *threat*, and it has to win any contest for the eye. A plate lit hard enough to compete
  // with the chamber turns the most important object in the game into two signals shouting
  // at once, so the plates are painted, not lit.
  applyColor(parts.team[0], chambered ? teamColor : mixHex(teamColor, PALETTE.ink, 0.52));
  applyEmissive(parts.team[0], teamColor, chambered ? 0.16 : 0.04);
  applyColor(parts.team[1], mixHex(teamColor, PALETTE.ink, chambered ? 0.25 : 0.66));
  applyEmissive(parts.team[1], teamColor, chambered ? 0.08 : 0.01);

  // Energy: hot amber at full emissive, or genuinely dead. Not dimmed — dead.
  //
  // The bloom mask goes with it. Left latched on, a spent gun's rail and flag — flat grey,
  // but grey in full sun — cleared the post chain's masked-emitter threshold discount and
  // kept a soft halo, so an empty gun still read as faintly lit from across the arena. The
  // brief for EMPTY is "dead flat unlit grey", and a halo is none of those four words.
  for (const m of parts.energy) setBloomMask(m, chambered ? 1 : 0);
  setBloomMask(parts.bore, chambered ? 1 : 0);
  applyColor(parts.energy[0], energyColor);
  applyEmissive(parts.energy[0], PALETTE.loaded, chambered ? HOT_EMISSIVE : DEAD_EMISSIVE);
  applyColor(parts.energy[1], energyColor);
  applyEmissive(parts.energy[1], PALETTE.loaded, chambered ? WARM_EMISSIVE : DEAD_EMISSIVE);

  // The bore is the exception to EMPTY grey: down the barrel, a spent gun must be a hole.
  applyColor(parts.bore, chambered ? energyColor : bodyHex(BODY_DEEP_LIFT));
  applyEmissive(parts.bore, PALETTE.loaded, chambered ? WARM_EMISSIVE : DEAD_EMISSIVE);

  // Chamber window: a blazing aperture, or a cold dark pane. The additive pane wants the
  // full LOADED amber, not the darkened albedo — nothing lights it, it simply adds. Held
  // back from the old 0.62 because the window grew: additive over the emissive shell behind
  // it, a hotter pane drove the aperture past amber into white.
  const glass = parts.glass[0] as THREE.MeshBasicMaterial;
  glass.color.setHex(chambered ? PALETTE.loaded : EMPTY_METAL);
  glass.opacity = chambered ? 0.5 : 0.12;
  glass.blending = chambered ? THREE.AdditiveBlending : THREE.NormalBlending;
  glass.needsUpdate = true;

  // Core: a fat spinning spindle, or a collapsed sliver.
  parts.core.scale.setScalar(chambered ? 1 : 0.42);
  parts.shards.visible = chambered;

  // Barrel rings leave the silhouette entirely when empty.
  parts.rings.scale.setScalar(chambered ? 1 : 0.001);
  // …and the chamber flag drops. This is the one state cue that survives greyscale, eight
  // pixels and colour-blindness, because it changes the OUTLINE and not the colour.
  parts.flag.rotation.x = chambered ? FLAG_UP : FLAG_DOWN;
  parts.pip.scale.set(1, chambered ? 1 : 0.35, 1);

  // Long-range read.
  parts.glow.material.color.setHex(PALETTE.loaded);
  parts.glow.material.opacity = chambered ? 1 : 0;
  parts.glow.visible = chambered;
  parts.glow.scale.setScalar(chambered ? GLOW_CORE_SCALE : 0.01);

  parts.halo.material.color.setHex(PALETTE.loaded);
  parts.halo.material.opacity = chambered ? GLOW_HALO_OPACITY : 0;
  parts.halo.visible = chambered;
  parts.halo.scale.setScalar(chambered ? GLOW_HALO_SCALE : 0.01);
}

function applyColor(m: THREE.Material | undefined, hex: number): void {
  if (!m) return;
  const t = m as THREE.Material & { color?: THREE.Color };
  if (t.color) t.color.setHex(hex);
}

function applyEmissive(m: THREE.Material | undefined, hex: number, intensity: number): void {
  if (!m) return;
  const t = m as THREE.Material & { emissive?: THREE.Color; emissiveIntensity?: number };
  if (t.emissive) t.emissive.setHex(hex);
  if ('emissiveIntensity' in t) t.emissiveIntensity = intensity;
}

// ---------------------------------------------------------------------------
// Idle animation
// ---------------------------------------------------------------------------

/**
 * Breathe life into a chambered gun: spin the core, orbit the shards, and pulse the glow.
 * A static glow reads as a decal; a moving one reads as contained energy.
 *
 * Not part of the fixed module contract — call it from whatever owns the gun's frame loop
 * (`PlayerRig` and `ViewModel` both do). Safe and free to call on an empty gun.
 *
 * @param timeSec monotonically increasing seconds; any shared clock will do.
 * @param glowScale multiplier on the two additive glow sprites. The sprites are sized for
 *   the *arena* read — a gun forty metres away that has to resolve as an amber dot — and at
 *   that size they are far too big for a gun held thirty centimetres from the eye, where
 *   they would additively wash out the lower-right quadrant of the frame and bleach the
 *   dark body they exist to contrast against. First-person callers pass a small value and
 *   let the emissive geometry carry the close read; world and rig guns leave it at 1.
 */
export function animateGun(g: THREE.Group, timeSec: number, glowScale = 1): void {
  const parts = partsOf(g);
  if (!parts || !parts.chambered) return;

  parts.core.rotation.z = timeSec * 7.5;
  parts.shards.rotation.z = -timeSec * 3.1;
  parts.shards.rotation.x = Math.sin(timeSec * 1.7) * 0.4;

  // Two detuned sines so the pulse never looks metronomic.
  const pulse = clamp01(0.5 + 0.3 * Math.sin(timeSec * 6.2) + 0.2 * Math.sin(timeSec * 9.7));

  // The chamber *breathes*. Every emissive part rides the same envelope, so the rail, the
  // rings, the core and the exhaust port all swell together and the whole gun reads as one
  // energised object rather than a shell with lamps glued to it. Subtle on purpose — a hard
  // blink would be mistaken for a state change, and the state change is the one thing on
  // this object that must never be ambiguous.
  const breath = 1 - PULSE_DEPTH + pulse * PULSE_DEPTH * 2;
  applyEmissive(parts.energy[0], PALETTE.loaded, HOT_EMISSIVE * breath);
  applyEmissive(parts.energy[1], PALETTE.loaded, WARM_EMISSIVE * breath);
  applyEmissive(parts.bore, PALETTE.loaded, WARM_EMISSIVE * breath);

  const gs = glowScale > 0 ? glowScale : 0;
  parts.glow.scale.setScalar(GLOW_CORE_SCALE * gs * (0.82 + pulse * 0.32));
  parts.glow.material.opacity = 0.72 + pulse * 0.28;
  // The halo breathes on the opposite phase, so the two never pop together and read as one
  // hard blink.
  parts.halo.scale.setScalar(GLOW_HALO_SCALE * gs * (1.06 - pulse * 0.22));
  parts.halo.material.opacity = GLOW_HALO_OPACITY * (0.8 + (1 - pulse) * 0.5);
}

/**
 * Release everything a gun mesh owns. Not part of the fixed contract, but every owner of a
 * gun mesh (rig, viewmodel, world-entity pool) needs it — the materials are per-instance.
 */
export function disposeGunMesh(g: THREE.Group): void {
  const parts = partsOf(g);
  if (parts) delete g.userData['gunParts'];
  disposeObject3D(g);
}
